import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Mail, Search, Send } from 'lucide-react';

const dateFr = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : null);

// Envoi manuel du livret (PDF joint) à plusieurs candidats, avec suivi « Envoyé le … »
export const EnvoiLivretCandidats = ({ publie }) => {
  const [data, setData] = useState({ candidats: [], sessions: [] });
  const [q, setQ] = useState('');
  const [session, setSession] = useState('');
  const [choix, setChoix] = useState(new Set());
  const [envoi, setEnvoi] = useState(false);
  const charger = useCallback(() => { axios.get('/livret-accueil/envois').then(({ data: d }) => setData(d)).catch(() => {}); }, []);
  useEffect(() => { charger(); }, [charger]);

  const liste = useMemo(() => data.candidats.filter(c => (!session || c.session === session)
    && `${c.prenom} ${c.nom} ${c.email}`.toLowerCase().includes(q.toLowerCase())), [data, q, session]);
  const tousCoches = liste.length > 0 && liste.every(c => choix.has(c.user_id));
  const basculer = (id) => setChoix(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toutBasculer = () => setChoix(tousCoches ? new Set() : new Set(liste.map(c => c.user_id)));

  const envoyer = async () => {
    if (!window.confirm(`Envoyer le livret d'accueil par e-mail à ${choix.size} candidat(s) ?`)) return;
    setEnvoi(true);
    try {
      const { data: r } = await axios.post('/livret-accueil/envoyer', { candidat_ids: [...choix] });
      toast.success(`Livret envoyé à ${r.envoyes} candidat(s)`);
      if (r.sans_email.length) toast.warning(`Sans e-mail (notification seulement) : ${r.sans_email.join(', ')}`);
      setChoix(new Set());
      charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible");
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="livret-envoi-candidats">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Mail className="h-5 w-5 text-[#8DC63F]" />
        <h2 className="flex-1 text-lg font-bold text-slate-800">Envoyer le livret aux candidats</h2>
        <button type="button" onClick={envoyer} disabled={!publie || !choix.size || envoi} data-testid="livret-envoyer"
          className="inline-flex items-center gap-2 rounded-full bg-[#8DC63F] px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-[#7AB62F] disabled:opacity-50">
          {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer ({choix.size})
        </button>
      </div>
      {!publie && <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="livret-envoi-non-publie">Publiez d'abord le livret pour pouvoir l'envoyer.</p>}
      <div className="mb-3 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un candidat" data-testid="livret-envoi-recherche"
            className="h-9 w-full rounded-lg border border-slate-200 pl-9 pr-3 text-sm" />
        </div>
        <select value={session} onChange={e => setSession(e.target.value)} data-testid="livret-envoi-session" className="h-9 rounded-lg border border-slate-200 px-3 text-sm">
          <option value="">Toutes les sessions</option>
          {data.sessions.map(s => <option key={s} value={s}>Session {s}</option>)}
        </select>
      </div>
      <div className="max-h-[420px] overflow-y-auto rounded-lg border border-slate-100">
        <label className="flex items-center gap-3 border-b bg-slate-50 px-3 py-2 text-xs font-semibold uppercase text-slate-500">
          <input type="checkbox" checked={tousCoches} onChange={toutBasculer} data-testid="livret-envoi-tout" /> Tout cocher ({liste.length})
        </label>
        {liste.map(c => (
          <label key={c.user_id} className="flex cursor-pointer flex-wrap items-center gap-x-3 gap-y-1 border-b px-3 py-2 text-sm hover:bg-slate-50" data-testid={`livret-envoi-ligne-${c.user_id}`}>
            <input type="checkbox" checked={choix.has(c.user_id)} onChange={() => basculer(c.user_id)} data-testid={`livret-envoi-check-${c.user_id}`} />
            <span className="min-w-0 flex-1"><span className="font-medium text-slate-800">{c.prenom} {c.nom}</span> <span className="break-all text-xs text-slate-400">{c.email}</span></span>
            <span className="flex w-full items-center gap-3 pl-7 sm:w-auto sm:pl-0">
              {c.session && <span className="text-xs text-slate-400">Session {c.session}</span>}
              <span className={`text-xs font-medium ${c.envoye_le ? 'text-emerald-600' : 'text-slate-400'}`} data-testid={`livret-envoi-statut-${c.user_id}`}>
                {c.envoye_le ? `Envoyé le ${dateFr(c.envoye_le)}${c.nb_envois > 1 ? ` (${c.nb_envois} fois)` : ''}` : 'Non envoyé'}
              </span>
            </span>
          </label>
        ))}
        {liste.length === 0 && <p className="p-4 text-center text-sm text-slate-400">Aucun candidat</p>}
      </div>
    </section>
  );
};
